import UUID from 'uuid-js'

export function UUIDV4() {
  return UUID.create().toString()
}

export const Storage = {
  get: <T = any>(key: string, storage: Storage = localStorage): T => {
    const value = storage.getItem(key)
    try {
      if (value) return JSON.parse(value) as T
      return value as any
    } catch (err) {
      return value as any
    }
  },
  set: (key: string, value: unknown, storage: Storage = localStorage): void => {
    if (isNull(value)) {
      storage.removeItem(key)
      return
    }
    storage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value.toString())
  },
  remove: (key: string, storage: Storage = localStorage) => {
    storage.removeItem(key)
  },
}

export const SessionStorage = {
  get: <T = any>(key: string) => Storage.get<T>(key, sessionStorage),
  set: (key: string, value: any) => Storage.set(key, value, sessionStorage),
  remove: (key: string): void => Storage.remove(key, sessionStorage),
}

/**
 * 格式化请求参数，去除值为空和'_'开头的变量
 */
export function formatQuery(query: object) {
  return JSON.parse(
    JSON.stringify(query, (key, value) =>
      !['', null].includes(value) && !key.startsWith('_') ? value : undefined
    )
  )
}

/**
 * 格式化请求参数,将值('',null)替换为undefined)
 */
export function formatNullQuery(query: Record<string, any>) {
  Object.keys(query).forEach((key) => {
    if (['', null].includes(query[key])) {
      query[key] = undefined
    }
  })
  return query
}

export function copy<T>(obj: T): T {
  return typeof obj === 'object' ? JSON.parse(JSON.stringify(obj)) : obj
}

export function parseNumber(n: string | number, m = 0) {
  return parseFloat(parseFloat(n as string).toFixed(m))
}

/**
 * 数组去重
 * @param {Array} items 原数组
 * @param {Function => Boolean} predict 去重判断方法
 * @returns {Array} 去重后的数组
 */
export function deduplicate<T = any>(items: T[], predict: (a: T, b: T) => boolean) {
  return items.filter((item, index) => items.findIndex((it) => predict(it, item)) === index)
}

// 生成树状结构
export function parseTreeData<T extends { id: string; parentId: string; children: T[] } = any>(
  items: T[]
) {
  if (!items) items = []
  const treeItems: T[] = []
  items.forEach((item) => {
    const parentId = item.parentId
    if (!parentId || parentId === '0') {
      treeItems.push(item)
    } else {
      let parentItem = items.find((it) => it.id === parentId)
      if (parentItem) {
        ;(parentItem.children = parentItem.children || []).push(item)
      }
    }
  })
  return {
    treeItems: treeItems,
    flatItems: items,
  }
}

/**
 * 树状结构扁平化
 */
export function flatTreeData<T extends object = any>(treeItems: T[], childrenKey?: keyof T) {
  if (!childrenKey) childrenKey = 'children' as keyof T
  const flatItems: T[] = []
  function loop(items: T[]) {
    if (!items?.length) return
    items.forEach((item) => {
      flatItems.push(item)
      loop(item[childrenKey] as unknown as T[])
    })
  }
  loop(treeItems)
  return flatItems
}

/**
 * 根据条件过滤树状结构
 * @param treeItems 初始树数组
 * @param fn: (item) => boolean 是否能保留
 */
export function filterTree<T extends { children?: T[] }>(treeItems: T[], fn: (item: T) => boolean) {
  function loop(items: T[], parent?: T) {
    if (!items?.length) return []
    const addItems: T[] = []
    items.forEach((item) => {
      const childCount = item.children?.length
      const cpItem = { ...item }
      const childAddCount = childCount && loop(item.children, cpItem).length
      if (!childCount || childAddCount) {
        if (fn(item)) {
          addItems.push(cpItem)
        }
      }
    })
    if (parent) parent.children = addItems
    return addItems
  }
  return loop(treeItems)
}

/**
 * 下载文件
 */
export function downloadFile(url: string, options?: { blank?: boolean; name?: string }) {
  const a = document.createElement('a')
  if (options.blank) a.setAttribute('target', '_blank')
  a.setAttribute('href', url)
  if (options.name) a.setAttribute('download', options.name)
  a.click()
}

/**
 * 拆分成数字格式
 */
function getVersionCodes(vName: string) {
  return vName.split('.').map((it) => parseInt(it))
}

/**
 * 比较版本名是否变大
 * @param vName1 最新版本名
 * @param vName2 与最新比较的
 */
export function compareVersion(vName1: string, vName2: string) {
  if (!vName1 && vName2) return true
  if (vName1 === vName2) return false
  const codes1 = getVersionCodes(vName1)
  const codes2 = getVersionCodes(vName2)
  for (let i = 0; i < codes1.length; i++) {
    const [c1, c2] = [codes1[i], codes2[i]]
    if (c2 > c1) return true
    if (c2 < c1) return false
  }
}

/**
 * 获取下个版本名, 注意不能超过255
 */
export function getNextVersion(vName: string) {
  const codes = getVersionCodes(vName);
  const newCodes: number[] = [];
  let versionNum = 0;
  for(let i = 0; i < codes.length; i++) {
    versionNum += codes[i] * 256**(codes.length - 1 - i);
  }
  versionNum += 1;
  for(let i = 0; i < 4; i++) {
    newCodes.unshift(versionNum & 0xff);
    versionNum = versionNum >>> 8;
  }
  return newCodes.join('.');
}

const KB = 1024
const MB = KB * 1024
const GB = MB * 1024

export const B2MB = (val: number) => parseNumber(val / MB, 1)

export const getFileSizeText = (size: number) => {
  if (size < KB) return `${parseNumber(size, 1)}B`
  if (size < MB) return `${parseNumber(size / KB, 1)}KB`
  if (size < GB) return `${parseNumber(size / MB, 2)}MB`
  return `${parseNumber(size / GB, 2)}GB`
}

export function isNull(val: any): boolean {
  return val === null || val === undefined || Number.isNaN(val)
}

export function hasProperties(obj: object) {
  if (!obj) return false
  return Object.keys(obj).length > 0
}

/**
 * 下载图片
 */
export function downloadImageByUrl (url: string, filename: string, fileType = 'png') {
  if (!url) throw new Error('当前没有下载链接');
  filename += `-${new Date().getTime()}.${fileType}`
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  // 使用target="_blank"时，添加rel="noopener noreferrer" 堵住钓鱼安全漏洞 防止新页面window指向之前的页面
  a.rel = "noopener noreferrer";
  document.body.append(a);
  a.click();
  setTimeout(() => { a.remove() }, 1000);
}

/**
 *  定时延迟
 */
export function sleep (delay: number = 1 * 1000) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(true), delay)
  })
}


